// src/components/Chat/SellSubscriptionDialog.tsx
import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { apiErrorMessage } from '../../api/errors';
import { fetchSubscriptionTypes, sellSubscription, SubscriptionType } from '../../api/subscriptions';
import { formatRubles } from './money';

interface SellSubscriptionDialogProps {
    open: boolean;
    onClose: () => void;
    /** Организация консоли: тарифы и продажи у каждой компании свои. */
    mode: string;
    /** Клиент уже есть в справочнике — продаём по его id. */
    clientContactId?: string | null;
    clientName?: string | null;
    clientPhone?: string | null;
    onSold: () => void;
}

const parseAmount = (raw: string): number | null => {
    const normalized = raw.replace(',', '.').trim();
    if (!normalized) return null;

    const value = Number(normalized);

    return Number.isFinite(value) ? value : null;
};

export const SellSubscriptionDialog: React.FC<SellSubscriptionDialogProps> = ({
    open,
    onClose,
    mode,
    clientContactId,
    clientName,
    clientPhone,
    onSold,
}) => {
    const [plans, setPlans] = useState<SubscriptionType[]>([]);
    const [subscriptionId, setSubscriptionId] = useState('');
    const [price, setPrice] = useState('');
    const [lessons, setLessons] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const applyPlan = useCallback((plan: SubscriptionType) => {
        setSubscriptionId(plan.id);
        // Цены абонементов ведутся в рублях: и в консоли, и в базе.
        setPrice(plan.price > 0 ? String(plan.price) : '');
        // В этом биллинге periodDays и есть количество занятий в абонементе:
        // бэкенд отдаёт его же как totalLessons проданного абонемента.
        setLessons(plan.periodDays > 0 ? String(plan.periodDays) : '10');
    }, []);

    useEffect(() => {
        if (!open) return;

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            setPlans([]);
            setSubscriptionId('');
            setPrice('');
            setLessons('');

            try {
                const list = await fetchSubscriptionTypes(mode);
                if (cancelled) return;

                // Проданные тарифы не предлагаем, но если активных нет —
                // показываем всё, что есть, чтобы было из чего выбрать.
                const active = list.filter((item) => item.isActive);
                const available = active.length > 0 ? active : list;

                setPlans(available);
                if (available.length > 0) applyPlan(available[0]);
            } catch (e) {
                if (!cancelled) setError(apiErrorMessage(e, 'Не удалось загрузить тарифы'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
    }, [open, mode, applyPlan]);

    const handlePlanChange = (nextId: string) => {
        const plan = plans.find((item) => item.id === nextId);
        if (plan) applyPlan(plan);
    };

    const handleSubmit = async () => {
        if (!mode) {
            setError('Выберите организацию.');
            return;
        }
        if (!subscriptionId) {
            setError('Выберите абонемент.');
            return;
        }

        const priceRubles = parseAmount(price);
        if (priceRubles === null || priceRubles < 0) {
            setError('Укажите цену абонемента.');
            return;
        }

        const lessonsCount = parseAmount(lessons);
        if (lessonsCount === null || lessonsCount < 0) {
            setError('Укажите количество занятий.');
            return;
        }

        const trimmedName = (clientName ?? '').trim();
        const trimmedPhone = (clientPhone ?? '').trim();
        if (!clientContactId && (!trimmedName || !trimmedPhone)) {
            setError('Для продажи нужны имя и телефон клиента.');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            await sellSubscription(mode, {
                subscriptionId,
                totalPrice: Math.round(priceRubles),
                remainingLessons: Math.round(lessonsCount),
                ...(clientContactId
                    ? { clientContactId }
                    : { clientName: trimmedName, clientPhone: trimmedPhone }),
            });
            onSold();
        } catch (e) {
            setError(apiErrorMessage(e, 'Не удалось продать абонемент'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Продать абонемент</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : null}
                    {!loading && plans.length === 0 && !error ? (
                        <Typography variant="body2" color="text.secondary">
                            У организации нет тарифов — сначала заведите абонемент в настройках.
                        </Typography>
                    ) : null}
                    {plans.length > 0 ? (
                        <TextField
                            select
                            fullWidth
                            label="Абонемент"
                            value={subscriptionId}
                            onChange={(event) => handlePlanChange(event.target.value)}
                            disabled={saving || loading}
                        >
                            {plans.map((plan) => (
                                <MenuItem key={plan.id} value={plan.id}>
                                    {`${plan.name} — ${formatRubles(plan.price, plan.currency)}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : null}
                    <TextField
                        fullWidth
                        label="Цена, ₽"
                        type="number"
                        value={price}
                        onChange={(event) => setPrice(event.target.value)}
                        disabled={saving || loading}
                        inputProps={{ min: 0, step: 1 }}
                    />
                    <TextField
                        fullWidth
                        label="Количество занятий"
                        type="number"
                        value={lessons}
                        onChange={(event) => setLessons(event.target.value)}
                        disabled={saving || loading}
                        inputProps={{ min: 0, step: 1 }}
                    />
                    {!clientContactId ? (
                        <Alert severity="info">
                            Клиента ещё нет в справочнике — он будет создан автоматически
                            {clientName && clientPhone ? ` (${clientName}, ${clientPhone})` : ''}.
                        </Alert>
                    ) : null}
                    {error ? <Alert severity="error">{error}</Alert> : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void handleSubmit()}
                    disabled={saving || loading || !mode || plans.length === 0}
                    startIcon={saving ? <CircularProgress color="inherit" size={16} /> : undefined}
                >
                    Продать
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SellSubscriptionDialog;
